import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import Input from '../../global/Input';
import {
    useCreateMeasureMutation,
    useGetMeasureTypesQuery,
    useGetPFAPGVKMTypesQuery,
    useGetRealisationTypesQuery,
    useGetTimePeriodTypesQuery,
    useUpdateMeasureMutation
} from '../../../redux/rtk/injectedMeasureApi';
import {useCleanArray} from '../../hooks/useCleanArray';
import {showMessageOnError, showMessageOnSuccess} from '../../global/CustomToast';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';

import {checkPermission, saveMappings} from '../../../functions/functionLibrary';
import {useDeleteMappingMutation, useUpdateMappingMutation} from '../../../redux/rtk/injectedMappingApi';
import {useGetBundeslandQuery} from '../../../redux/rtk/injectedIsoBundeslandApi';
import {Accordion, AccordionTab} from 'primereact/accordion';
import StatusLight from '../../global/StatusLight';
import {Button} from 'primereact/button';
import CommentHistory from '../../global/CommentHistory';
import EditDialogLayout from '../../global/EditDialogLayout';
import {Measure} from '../../functions/ProjectMasterData';
import FormPrompt from '../../global/FormPrompt';
import {UtilisationMeasureMapping} from '../mappings/UtilisationMeasureMapping';
import {MappingType} from '../../../redux/slices/MappingSlice';
import {PersonMapping} from '../../globalData/person/PersonMapping';
import moment from 'moment';
import {TabPanel, TabView} from 'primereact/tabview';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDatabase, faList, faMapLocation, faProjectDiagram} from '@fortawesome/free-solid-svg-icons';
import MapOl from '../../geoData/map';
import LayersMapOl from '../../geoData/layer/LayersMapOl';
import BaseLayers from '../../geoData/layer/BaseLayers';
import PoiLayer from '../../geoData/layer/PoiLayer';
import SingleLayer from '../../geoData/layer/SingleLayer';
import geoSettings from '../../../config/geoSettings';
import {LandRegisterMapping} from '../../globalData/landRegister/LandRegisterMapping';
import {ParcelMapping} from '../../globalData/parcel/ParcelMapping';
import CautionSign from '../../global/CautionSign';
import {RadioButton} from 'primereact/radiobutton';
import {classNames} from 'primereact/utils';
import {Nullable} from '../../functions/Global';
import {permissions} from '../../../config/permissions';
import {MeasureOverview} from './MeasureOverview';
import {UtilisationOverview} from '../utilisation/UtilisationOverview';
import {PersonOverview} from '../../globalData/person/PersonOverview';
import {ParcelOverview} from '../../globalData/parcel/ParcelOverview';
import {LandRegisterOverview} from '../../globalData/landRegister/LandRegisterOverview';
import UnderConstruction from '../../../views/pages/UnderConstruction';

type Props = {
    projectId: string,
    data?: Measure,
    setVisible: any,
    setIsDirty: (isDirty: boolean) => void
    onFinished: (success: any) => void
}
export const MeasureEditForm = (props: Props): JSX.Element => {
    const {t} = useTranslation(['common', 'projectMasterData']);

    const [createMeasure] = useCreateMeasureMutation();
    const [updateMeasure] = useUpdateMeasureMutation();

    const measureTypesQuery = useGetMeasureTypesQuery({projectId: props.projectId});

    const [data, setData] = useState<Measure | undefined>(undefined);
    const [height, setHeight] = useState<number>(0);
    const [measureTypes, setMeasureTypes] = useState<any>([]);
    const [mappingChanged, setMappingChanged] = useState<boolean>(false);

    const [utilMapping, setutilMapping] = useState<any[]>([]);

    const [timePeriodTypes, setTimePeriodTypes] = useState<any[]>([]);
    const [realisationTypes, setRealisationTypes] = useState<any[]>([]);
    const [pfaTypes, setPfaTypes] = useState<any[]>([]);

    const timePeriodTypesQuery = useGetTimePeriodTypesQuery({});
    const realisationTypesQuery = useGetRealisationTypesQuery({});
    const pfaTypesQuery = useGetPFAPGVKMTypesQuery({});

    useEffect(() => {
        if (timePeriodTypesQuery.data) {
            setTimePeriodTypes(timePeriodTypesQuery.data.map((x: any) => {
                return {label: x.name, value: x.id};
            }));
        }
    }, [timePeriodTypesQuery]);

    useEffect(() => {
        if (realisationTypesQuery.data) {
            setRealisationTypes(realisationTypesQuery.data.map((x: any) => {
                return {label: x.name, value: x.id};
            }));
        }
    }, [realisationTypesQuery]);

    useEffect(() => {
        if (pfaTypesQuery.data) {
            setPfaTypes(pfaTypesQuery.data.map((x: any) => {
                return {label: x.name, value: x.id};
            }));
        }
    }, [pfaTypesQuery]);

    const divRef: any = useRef(null);

    const isCleaned = useCleanArray(data, {
        massnahme_id: props.data ? props.data.massnahme_id : undefined,
        project_id: props.projectId
    }, true);

    const defaultValues: Measure = {
        'massnahme_id': props.data ? props.data.massnahme_id : '',
        'project_id': props.data ? props.data.project_id : '',
        'massnahme_type_name': props.data ? props.data.massnahme_type_name : '',
        'massnahme_type_id': props.data ? props.data.massnahme_type_id : 0,
        'umsetzung_type_id': props.data ? props.data.umsetzung_type_id : 0,
        'zeitraum_type_id': props.data ? props.data.zeitraum_type_id : 0,
        'name': props.data ? props.data.name : '',
        'bwv_lf_nr': props.data ? props.data.bwv_lf_nr : '',
        'bwv_sub_lf_nr': props.data ? props.data.bwv_sub_lf_nr : '',
        'bwv_stand': props.data ? props.data.bwv_stand : '',
        'plan_name': props.data ? props.data.plan_name : '',
        'plan_stand': props.data ? props.data.plan_stand : '',
        'plan_stand_datum': props.data && props.data.plan_stand_datum != null ? new Date(props.data.plan_stand_datum) : null,
        'pfa_pgv_stand_start': props.data ? props.data.pfa_pgv_stand_start : 0,
        'pfa_pgv_stand_ende': props.data ? props.data.pfa_pgv_stand_ende : 0,
        'pfa_pgv_status_id': props.data ? props.data.pfa_pgv_status_id : '',
        'pfa_pgv_name': props.data ? props.data.pfa_pgv_name : '',
        'pfa_pgv_stand': props.data ? props.data.pfa_pgv_stand : '',
        'pfa_pgv_datum': props.data ? new Date(props.data.plan_stand) : null,
        'flaechenbezug_type_id': props.data ? props.data.flaechenbezug_type_id : '',
        'flaechenbezug_type_name': props.data ? props.data.flaechenbezug_type_name : '',
        'start_datum_soll': props.data && props.data.start_datum_soll != null ? new Date(props.data.start_datum_soll) : null,
        'start_datum_ist': props.data && props.data.start_datum_ist != null ? new Date(props.data.start_datum_ist) : null,
        'ende_datum_soll': props.data && props.data.ende_datum_soll != null ? new Date(props.data.ende_datum_soll) : null,
        'ende_datum_ist': props.data && props.data.ende_datum_ist != null ? new Date(props.data.ende_datum_ist) : null,
        'flurstueck': props.data ? props.data.flurstueck : []
    };

    const {
        control,
        formState: {errors, isDirty},
        handleSubmit,
        setValue,
        getValues,
        reset,
        watch
    } = useForm({defaultValues});

    const zeitraumType = watch('zeitraum_type_id');
    const statusId = 0;

    const saveData = (data: any) => {
        // eslint-disable-next-line camelcase

        if (utilMapping.length > 0) {

        }

        setData(data);
    };

    const getFormErrorMessage = (name: string) => {
        // @ts-ignore
        return errors[name] && <small className="p-error custom">{errors[name].message}</small>;
    };

    useEffect(() => {
        props.setIsDirty(isDirty || mappingChanged);
    }, [isDirty, mappingChanged]);

    useEffect(() => {
        if (divRef.current) {
            setHeight(divRef.current.parentElement.clientHeight);
        }
    }, [divRef.current]);

    useEffect(() => {
        if (typeof data !== 'undefined') {
            if (props.data) {
                updateMeasure(isCleaned).then(async (result: any) => {
                    if (result.error) {
                        showMessageOnError(JSON.stringify(result.error.data), '');
                    } else {
                        showMessageOnSuccess(t('projectMasterData:measures.updated'), '');
                        console.log(isCleaned);
                        props.onFinished(isCleaned);
                    }
                });
            } else {
                createMeasure(isCleaned).then(async (result: any) => {
                    if (result.error) {
                        showMessageOnError(JSON.stringify(result.error.data), '');
                    } else {
                        showMessageOnSuccess(t('projectMasterData:measures.created'), '');
                        props.onFinished(isCleaned);
                    }
                });
            }
        }
    }, [data]);

    useEffect(() => {
        if (measureTypesQuery.data) {
            setMeasureTypes(measureTypesQuery.data.map((x: any) => {
                return {label: x.name, value: x.id};
            }));
        }
    }, [measureTypesQuery]);

    const getStatus = () => {
        return (<StatusLight id={'statuslight-measureeditform'} value={statusId} info={'Testinfo'} light={false}
                             horizontal={false} caution={false}/>);
    };

    return (<div className={'relative h-full'}>
            <form ref={divRef} id="editMeasureForm" onSubmit={handleSubmit(saveData)} className={'h-full'}>
                <FormPrompt hasUnsavedChanges={isDirty || mappingChanged}/>
                <EditDialogLayout left={<>
                    <div className={'grid'}>
                        <div className={'col-12 md:col-6 grid grid-nogutter'}>
                            <div className={'col-fixed'}>
                                {getStatus()}
                            </div>
                            <div className={'col-auto'}>
                                <h5 className={'mb-1'}>{t('timerange')}</h5>
                                {timePeriodTypesQuery.data && timePeriodTypesQuery.data.map((item: any) => {
                                    return (<div className={'p-1'}>
                                        <Controller name="zeitraum_type_id" control={control}
                                                    rules={{
                                                        min: {
                                                            value: 1,
                                                            message: t('projectMasterData:measures.columns.zeitraum_type_id') + ' ' + t('input:required')
                                                        },
                                                        required: t('projectMasterData:measures.columns.zeitraum_type_id') + ' ' + t('input:required')
                                                    }}
                                                    render={({field, fieldState}) => (
                                                        <RadioButton
                                                            className={classNames('mr-2', errors['zeitraum_type_id'] ? 'error' : '')}
                                                            value={item.id}
                                                            inputId={'zeitraum_type_id' + item.id}
                                                            name="zeitraum_type_id"
                                                            onChange={(e) => {
                                                                field.onChange(item.id);
                                                            }}
                                                            checked={zeitraumType === item.id}/>)}/>
                                        <label
                                            htmlFor={'juristisch_type_id_' + item.id}>{item.name}</label>
                                    </div>);
                                })}
                            </div>
                        </div>
                        <div className={'col-12 md:col-6'}>
                            <Input
                                className={'pb-2'}
                                edit={checkPermission(permissions.editMeasures)}
                                label={t('projectMasterData:measures.columns.name')} name={'name'}
                                validationControl={control} validationErrorMessage={getFormErrorMessage}
                                validationRules={{
                                    required: t('projectMasterData:measures.columns.name') + ' ' + t('input:required')
                                }}
                            />
                            <Input
                                className={'pb-2'}
                                edit={checkPermission(permissions.editMeasures)}
                                label={t('projectMasterData:measures.columns.massnahme_type_name')}
                                name={'massnahme_type_id'}
                                type={'dropdown'}
                                dropdownOptions={measureTypes}
                                validationControl={control} validationErrorMessage={getFormErrorMessage}
                                validationRules={{
                                    required: t('projectMasterData:measures.columns.massnahme_type_name') + ' ' + t('input:required')
                                }}
                            />
                            <Input
                                edit={checkPermission(permissions.editMeasures)}
                                label={t('projectMasterData:measures.columns.umsetzung_type_id')}
                                name={'flaechenbezug_type_id'}
                                type={'dropdown'}
                                dropdownOptions={realisationTypes}
                                validationControl={control} validationErrorMessage={getFormErrorMessage}
                                validationRules={{
                                    // required: t('projectMasterData:measures.columns.flaechenbezug_type_name') + ' ' + t('input:required')
                                }}
                            />
                        </div>
                    </div>
                    <br/>
                    <Accordion>
                        <AccordionTab header={t('projectMasterData:measures.columns.bwv')}>
                            <div className={'grid'}>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)}
                                        label={t('projectMasterData:measures.columns.bwv_lf_nr')}
                                        name={'bwv_lf_nr'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.bwv_lf_nr') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)}
                                        label={t('projectMasterData:measures.columns.bwv_sub_lf_nr')}
                                        name={'bwv_sub_lf_nr'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.bwv_sub_lf_nr') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)} label={t('state')}
                                        name={'bwv_stand'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.bwv_stand') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                            </div>
                        </AccordionTab>
                    </Accordion>
                    <br/>
                    <Accordion>
                        <AccordionTab header={t('projectMasterData:measures.columns.plan')}>
                            <div className={'grid'}>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)} label={t('designation')}
                                        name={'plan_name'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.plan_name') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)} label={t('state')}
                                        name={'plan_stand'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.plan_stand') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)} label={t('date')}
                                        name={'plan_stand_datum'}
                                        type={'date'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.plan_stand_datum') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                            </div>
                        </AccordionTab>
                    </Accordion>
                    <br/>
                    <Accordion>
                        <AccordionTab header={t('projectMasterData:measures.columns.pfa_pgv')}>
                            <div className={'grid'}>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)} label={t('designation')}
                                        name={'pfa_pgv_name'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.pfa_pgv_name') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <div className={'grid'}>
                                        <div className={'col-12 md:col-6'}>
                                            <Input
                                                edit={checkPermission(permissions.editMeasures)}
                                                label={t('state') + ' ' + t('from')}
                                                name={'pfa_pgv_stand_start'}
                                                type={'number'}
                                                validationControl={control} validationErrorMessage={getFormErrorMessage}
                                                validationRules={{
                                                    // required: t('projectMasterData:measures.columns.pfa_pgv_stand') + ' ' + t('input:required')
                                                }}
                                            />
                                        </div>
                                        <div className={'col-12 md:col-6'}>
                                            <Input
                                                edit={checkPermission(permissions.editMeasures)}
                                                label={t('state') + ' ' + t('to')}
                                                name={'pfa_pgv_stand_ende'}
                                                type={'number'}
                                                validationControl={control} validationErrorMessage={getFormErrorMessage}
                                                validationRules={{
                                                    // required: t('projectMasterData:measures.columns.pfa_pgv_stand') + ' ' + t('input:required')
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)} label={t('date')}
                                        name={'pfa_pgv_datum'}
                                        type={'date'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.pfa_pgv_datum') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editMeasures)} label={t('status')}
                                        name={'pfa_pgv_status_id'}
                                        type={'dropdown'}
                                        dropdownOptions={pfaTypes}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:measures.columns.pfa_pgv_datum') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                            </div>
                        </AccordionTab>
                    </Accordion>
                    {/*<br/>*/}
                    {/*<Accordion>*/}
                    {/*    <AccordionTab header={t('projectMasterData:measures.columns.termine')}>*/}
                    {/*        <div className={'grid'}>*/}
                    {/*            <div className={'col-12 md:col-6'}>*/}
                    {/*                <Input*/}
                    {/*                    edit={checkPermission(permissions.editMeasures)} label={t('projectMasterData:measures.columns.start_datum_soll')}*/}
                    {/*                    name={'start_datum_soll'}*/}
                    {/*                    type={'date'}*/}
                    {/*                    validationControl={control} validationErrorMessage={getFormErrorMessage}*/}
                    {/*                    validationRules={{*/}
                    {/*                        // required: t('projectMasterData:measures.columns.start_datum_soll') + ' ' + t('input:required')*/}
                    {/*                    }}*/}
                    {/*                />*/}
                    {/*            </div>*/}
                    {/*            <div className={'col-12 md:col-6'}>*/}
                    {/*                <Input*/}
                    {/*                    edit={checkPermission(permissions.editMeasures)} label={t('projectMasterData:measures.columns.start_datum_ist')}*/}
                    {/*                    name={'start_datum_ist'}*/}
                    {/*                    type={'date'}*/}
                    {/*                    validationControl={control} validationErrorMessage={getFormErrorMessage}*/}
                    {/*                    validationRules={{*/}
                    {/*                        // required: t('projectMasterData:measures.columns.start_datum_ist') + ' ' + t('input:required')*/}
                    {/*                    }}*/}
                    {/*                />*/}
                    {/*            </div>*/}
                    {/*            <div className={'col-12 md:col-6'}>*/}
                    {/*                <Input*/}
                    {/*                    edit={checkPermission(permissions.editMeasures)} label={t('projectMasterData:measures.columns.ende_datum_soll')}*/}
                    {/*                    name={'ende_datum_soll'}*/}
                    {/*                    type={'date'}*/}
                    {/*                    validationControl={control} validationErrorMessage={getFormErrorMessage}*/}
                    {/*                    validationRules={{*/}
                    {/*                        // required: t('projectMasterData:measures.columns.ende_datum_soll') + ' ' + t('input:required')*/}
                    {/*                    }}*/}
                    {/*                />*/}
                    {/*            </div>*/}
                    {/*            <div className={'col-12 md:col-6'}>*/}
                    {/*                <Input*/}
                    {/*                    edit={checkPermission(permissions.editMeasures)} label={t('projectMasterData:measures.columns.ende_datum_ist')}*/}
                    {/*                    name={'ende_datum_ist'}*/}
                    {/*                    type={'date'}*/}
                    {/*                    validationControl={control} validationErrorMessage={getFormErrorMessage}*/}
                    {/*                    validationRules={{*/}
                    {/*                        // required: t('projectMasterData:measures.columns.ende_datum_ist') + ' ' + t('input:required')*/}
                    {/*                    }}*/}
                    {/*                />*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    </AccordionTab>*/}
                    {/*</Accordion>*/}
                </>}
                                  right={<TabView activeIndex={0} renderActiveOnly={false}>
                                      <TabPanel header={<div><FontAwesomeIcon className="sidebar-icon mr-2"
                                                                              icon={faDatabase}/>{t('tabs.relationshipProjectMasterData')}
                                      </div>}>
                                          <br/>
                                          <Accordion activeIndex={0}>
                                              <AccordionTab header={t('projectMasterData:utilisation.plural')}>
                                                  <UtilisationOverview
                                                      projectId={props.projectId}
                                                      mapping={data ? {massnahme_id: data.massnahme_id} : {}}
                                                      editable={false}
                                                      expandable={false}
                                                      hideGlobalFilter={false}
                                                      showColumnFilters={true}
                                                      hideButtons={true}
                                                      skeletonCount={1}
                                                      showHeader={false}
                                                  />
                                              </AccordionTab>
                                          </Accordion>
                                      </TabPanel>
                                  </TabView>}/>
            </form>
        </div>
    );
};
